import { ComposedChart, Line, ResponsiveContainer, XAxis } from 'recharts';
import { Bar, CartesianGrid, Legend, Tooltip, YAxis } from 'recharts';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { format } from 'date-fns';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import { BuildingType, DataType } from '../OverviewReport.types';
import { GraphWrapper, type GraphWrapperInfoCard } from '../../../../SharedComponents/GraphWrapper';
import { MonthAndYearCustomTick } from '../../../../SharedComponents/MonthAndYearCustomTick';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { StackedBarShape } from '../../../../SharedComponents/StackedBarShape';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { formatStackedNumbers } from '../../../../../utils';
import { isAllSingleFamily } from '../../../../../utils/isAllSingleFamily';
import { roundUp } from '../../../../../utils/roundUp';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitOverviewImpressionsMarketAverageEvent } from '../../../../../analytics/events';
import type { ImpressionsChartData } from '../OverviewReport.data';

type ImpressionsProps = {
  selectedRegionId: number;
  chartData: Array<ImpressionsChartData>;
  isRegionPerformanceLoading: boolean;
  buildingTypeFilter: BuildingType;
  setBuildingTypeFilter: (value: BuildingType) => void;
  displayType: DataType;
  setDisplayType: (value: DataType) => void;
};

export const Impressions = ({
  selectedRegionId,
  chartData,
  isRegionPerformanceLoading,
  buildingTypeFilter,
  setBuildingTypeFilter,
  displayType,
  setDisplayType,
}: ImpressionsProps) => {
  const { user } = useRentalManagerUser();

  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();
  const lastUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioImpressionsLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketRentLastUpdated);
  const currentDateFormatted = format(new Date(), 'MMM yyyy');

  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);
  const allSingleFamily = isAllSingleFamily(filteredProperties);
  const effectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : buildingTypeFilter;

  const title = getGraphTitle({
    graphName: 'Impressions',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  const isLoading = isFetchingInitialData || isRegionPerformanceLoading;

  const mtdData = chartData.find((data) => data.name === currentDateFormatted);
  const mtdImpressions = mtdData?.['Month to date portfolio total'] || 0;

  const buildingTypeText = effectiveBuildingType === BuildingType.SINGLE_FAMILY ? 'single-family' : 'multifamily';

  let cards: Array<GraphWrapperInfoCard> = [
    {
      title: 'Total impressions MTD',
      infoText:
        'Total :buildingTypeText impressions for your portfolio or property in the selected market, month to date.'.replace(
          ':buildingTypeText',
          buildingTypeText,
        ),
      value: mtdImpressions,
    },
  ];

  if (displayType === DataType.MARKET_AVERAGE) {
    cards = [
      {
        title: 'Portfolio average',
        infoText:
          'Average :buildingTypeText impressions for your portfolio or property in the selected market, month to date.'.replace(
            ':buildingTypeText',
            buildingTypeText,
          ),
        value: roundUp(chartData.reduce((acc, curr) => acc + curr['Avg. impressions'], 0) / (chartData.length || 1), 0),
      },
      {
        title: 'Market average',
        infoText: 'Average :buildingTypeText impressions for the selected market, month to date.'.replace(
          ':buildingTypeText',
          buildingTypeText,
        ),
        value: roundUp(
          chartData.reduce((acc, curr) => acc + (curr['Median market impressions'] || 0), 0) /
            (chartData.length > 1 ? chartData.length - 1 : 1),
          0,
        ),
      },
    ];
  }

  const lastUpdateText = `${lastUpdatedDate && lastUpdatedMarketDate ? ` Portfolio data updated ${lastUpdatedDate}, market data updated ${lastUpdatedMarketDate}.` : ''}`;

  return (
    <GraphWrapper
      graphId="impressions"
      title={title}
      subtitle={`Impressions are the number of times your listings were shown to users.${lastUpdateText}`}
      radioButtonFilter={displayType}
      setRadioButtonFilter={(value: DataType) => {
        if (user) emitOverviewImpressionsMarketAverageEvent({ user });
        setDisplayType(value);
      }}
      buttonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? buildingTypeFilter : undefined}
      setButtonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? setBuildingTypeFilter : undefined}
      infoCards={displayType === DataType.PORTFOLIO_TOTAL ? cards : []}
      loading={isLoading}
    >
      <ResponsiveContainer width="100%" height={350}>
        <ComposedChart data={chartData}>
          <defs>
            <pattern
              id="stripe-impressions-portfolio"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#B88DFC" />
              <rect width="3" height="6" fill="#891EFA" />
            </pattern>
            <pattern
              id="stripe-impressions-avg"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#73D4E4" />
              <rect width="3" height="6" fill="#009AAE" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <YAxis tickFormatter={(value) => formatStackedNumbers(value)} />
          <Tooltip
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentMonth = displayLabel === currentDateFormatted;
                    if (displayType === DataType.PORTFOLIO_TOTAL) {
                      if (isCurrentMonth) {
                        // Show only MTD value for current month
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Month to date portfolio total',
                        );
                      } else {
                        // Show only regular value for previous months
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Portfolio total',
                        );
                      }
                    } else {
                      // MARKET_AVERAGE mode
                      if (isCurrentMonth) {
                        // Show only MTD avg value for current month
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Month to date median impressions',
                        );
                      } else {
                        // Show only regular avg value and line for previous months
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) =>
                            entry.name === 'Avg. impressions' || entry.name === 'Median market impressions',
                        );
                      }
                    }
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={
                  displayType === DataType.PORTFOLIO_TOTAL
                    ? {
                        'Month to date': 'striped',
                      }
                    : {
                        'Month to date': 'striped-teal',
                        'Median market impressions': 'line',
                      }
                }
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    if (displayType === DataType.PORTFOLIO_TOTAL) {
                      // Filter out individual month-to-date entry
                      const filtered = payload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value !== 'Month to date portfolio total',
                      );
                      // Add a combined "Month to date" entry if month-to-date data exists
                      const hasMTD = payload.some(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value === 'Month to date portfolio total',
                      );
                      if (hasMTD) {
                        filtered.push({
                          value: 'Month to date',
                          type: 'rect',
                          color: '#891EFA',
                        });
                      }
                      return filtered;
                    } else {
                      // MARKET_AVERAGE mode
                      // Filter out individual month-to-date entry
                      const filtered = payload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value !== 'Month to date median impressions',
                      );
                      // Add a combined "Month to date" entry before "Median market impressions"
                      const hasMTD = payload.some(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value === 'Month to date median impressions',
                      );
                      if (hasMTD) {
                        const marketAvgIndex = filtered.findIndex(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.value === 'Median market impressions',
                        );
                        const mtdEntry = {
                          value: 'Month to date',
                          type: 'rect',
                          color: '#009AAE',
                        };
                        if (marketAvgIndex >= 0) {
                          filtered.splice(marketAvgIndex, 0, mtdEntry);
                        } else {
                          filtered.push(mtdEntry);
                        }
                      }
                      return filtered;
                    }
                  }
                }
              />
            }
          />
          {displayType === DataType.PORTFOLIO_TOTAL ? (
            <>
              <Bar dataKey="Portfolio total" stackId="a" fill="#891EFA" shape={StackedBarShape} />
              <Bar
                dataKey="Month to date portfolio total"
                stackId="a"
                fill="url(#stripe-impressions-portfolio)"
                shape={StackedBarShape}
              />
            </>
          ) : (
            <>
              <Bar dataKey="Avg. impressions" stackId="a" fill="#009AAE" shape={StackedBarShape} />
              <Bar
                dataKey="Month to date median impressions"
                stackId="a"
                fill="url(#stripe-impressions-avg)"
                shape={StackedBarShape}
              />
              <Line type="linear" dataKey="Median market impressions" stroke="#111116" strokeWidth={3} />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
